// ContextFilterAndSort.js
import React, { useState,useEffect } from "react";
import { useTranslation } from 'react-i18next';

import {
  Menu,
  MenuItem,
  TextField,
  IconButton,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckIcon from "@mui/icons-material/Check";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { PLACEHOLDER, UI_TEXT } from "../../constants";

const ContextFilterAndSort = ({
  options,
  selectedOption,
  onSortChange,
  placeholder,
  searchValue,
  onSearchChange,
  showSort = false,
  showSearch = false,
  navbarHeight = 64,
}) => {
  const theme = useTheme();
 
  
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");


  const { t } = useTranslation();

  const dropdownOptions = options
    ?.filter((option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map((option, index) => ({
      key: index,
      text: option,
      value: option,
    }));

  // Close menu if scrolling would cause it to go above navbar
  useEffect(() => {
    const handleScroll = () => {
      if (filterAnchorEl) {
        const rect = filterAnchorEl.getBoundingClientRect();
        const menuWouldBeAt = rect.bottom;
        
        // Close menu if it would go above navbar
        if (menuWouldBeAt < navbarHeight + 10) {
          setFilterAnchorEl(null);
        }
      }
    };

    if (Boolean(filterAnchorEl)) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [filterAnchorEl, navbarHeight]);

  const handleFilterOpen = (event) => {
    // Check if there's enough space to open menu
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWouldBeAt = rect.bottom;
    
    if (menuWouldBeAt >= navbarHeight + 10) {
      setFilterAnchorEl(event.currentTarget);
    }
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (value) => {
    onSortChange(value);
    handleFilterClose();
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
        isolation: 'isolate',
        zIndex: 1,
        minHeight: '30px',
        flexShrink: 0,
        margin: 0,
      }}
    >
      {showSearch && (
        <TextField
          variant="outlined"
          size="small"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <Box display="flex" alignItems="center">
                  <IconButton size="small">
                    <SearchIcon />
                  </IconButton>
                  {showSort && (
                    <IconButton 
                      size="small" 
                      onClick={handleFilterOpen}
                      sx={{ ml: 0.5 }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  )}
                </Box>
              ),
            },
          }}
          sx={{

            width: "100%",

          }}
        />
      )}

      {/* Menu that moves with search bar and respects navbar boundary */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        // Keep menu attached to search bar
        disableScrollLock={true}
        // Position menu below the button
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        // Ensure menu stays with search bar
        disablePortal={false}
        PaperProps={{
          sx: {
            minWidth: '200px',
            maxHeight: `${Math.min(300, window.innerHeight - navbarHeight - 100)}px`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            zIndex: theme.zIndex.appBar - 1,
          }
        }}
        slotProps={{
          paper: {
            style: {
              maxHeight: `${Math.min(300, window.innerHeight - navbarHeight - 100)}px`,
              width: '200px',
            },
          },
        }}
        // Additional constraint to prevent opening above navbar
        marginThreshold={navbarHeight}
      >
        <Divider />
        
        <Typography variant="subtitle1" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          {t('UI_TEXT.SORT_OPTIONS')}
        </Typography>
        <Divider />

        {dropdownOptions?.length > 0 ? (
          dropdownOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleFilterChange(option.value)}
              sx={{
                py: 1,
                fontWeight: option.value === selectedOption 
                  ? theme.typography.fontWeightMedium 
                  : 'normal',
                backgroundColor: option.value === selectedOption 
                  ? 'action.selected' 
                  : 'inherit',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {option.value === selectedOption && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    mr: 1,
                    color: theme.palette.primary.main,
                  }}
                />
              )}
              {option.text}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('UI_TEXT.NO_RESULT_FOUND')}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

ContextFilterAndSort.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  selectedOption: PropTypes.string,
  onSortChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  showSort: PropTypes.bool,
  showSearch: PropTypes.bool,
  navbarHeight: PropTypes.number,
};

ContextFilterAndSort.defaultProps = {
  selectedOption: "",
  placeholder: PLACEHOLDER.SEARCH,
  showSort: true,
  showSearch: true,
  navbarHeight: 64,
};

export default ContextFilterAndSort;
